import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components';
import { contractApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSigningRoom, MAX_SIGNING_ROOM_ATTEMPTS } from '../hooks/useSigningRoom';
import { redirectToExternalSigning, type SigningHandoffState } from '../utils/signingNavigation';
import { signingProviderDisplayName } from '../utils/signingProvider';
import { signingProjectReturnPath, signingReturnResult } from '../utils/signingReturn';

export function SigningHandoff() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as SigningHandoffState | null;
  const storageKey = id ? `etappio-signing-handoff:${id}` : null;
  const stored = useMemo<SigningHandoffState | null>(() => {
    if (!storageKey) return null;
    try {
      const value = sessionStorage.getItem(storageKey);
      return value ? JSON.parse(value) as SigningHandoffState : null;
    } catch { return null; }
  }, [storageKey]);
  const handoff = state?.signingUrl ? state : stored;
  const [opened, setOpened] = useState(handoff?.autoOpen ?? false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (storageKey && state?.signingUrl) sessionStorage.setItem(storageKey, JSON.stringify(state));
  }, [state, storageKey]);

  const embedded = handoff?.provider === 'zigned';
  const room = useSigningRoom({
    contractId: id, processId: handoff?.processId, enabled: embedded && opened && !leaving,
    persona: user?.personaType,
    onComplete: contract => {
      if (storageKey) sessionStorage.removeItem(storageKey);
      navigate(signingProjectReturnPath(contract.projectId, contract.id, signingReturnResult(contract, user?.personaType)), { replace: true });
    },
  });
  const provider = signingProviderDisplayName(handoff?.provider) ?? t('contracts.signingProviders.external');
  const signingUrl = room.session?.signingUrl ?? handoff?.signingUrl ?? '';
  const returnPath = handoff?.returnPath?.startsWith('/projects') ? handoff.returnPath : '/projects';

  const returnToContract = async () => {
    setLeaving(true);
    if (id) {
      try {
        const synced = await contractApi.syncSigning(id, 10_000);
        if (storageKey) sessionStorage.removeItem(storageKey);
        navigate(signingProjectReturnPath(synced.contract.projectId, synced.contract.id, signingReturnResult(synced.contract, user?.personaType)), { replace: true });
        return;
      } catch { /* Returning remains possible while the provider is unavailable. */ }
    }
    navigate(returnPath);
  };

  const errorKey = room.error === 'open' ? 'openError' : room.error === 'status' ? 'statusError'
    : room.error === 'verification' ? 'verificationError' : 'loadError';

  return (
    <DashboardLayout>
      <section className="signing-room-page" data-testid="embedded-signing-page">
        <header className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
          <div className="min-w-0">
            <h1 className="h4 mb-1">{t('contracts.signingHandoff.title')}</h1>
            <p className="text-muted mb-0 text-break">{handoff?.contractLabel ?? t('contracts.signingHandoff.contractFallback')}</p>
          </div>
          <div className="d-flex flex-wrap gap-2 signing-room-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={leaving} onClick={() => void returnToContract()}>
              {t('contracts.signingHandoff.backToContract')}
            </button>
            {signingUrl && <button type="button" className="btn btn-outline-primary" onClick={() => redirectToExternalSigning(signingUrl)}>
              {t('contracts.signingRoom.openOutside')}
            </button>}
          </div>
        </header>

        {!signingUrl || (embedded && !handoff?.processId) ? (
          <div className="alert alert-warning">{t('contracts.signingHandoff.missingUrl')}</div>
        ) : !embedded ? (
          <button type="button" className="btn btn-primary" onClick={() => redirectToExternalSigning(signingUrl)}>
            {t('contracts.signingHandoff.openSigning')}
          </button>
        ) : !opened ? (
          <button type="button" className="btn btn-primary" onClick={() => setOpened(true)}>
            {t('contracts.signingHandoff.openSigning')}
          </button>
        ) : <>
          <p className="text-muted mb-2" role="status" aria-live="polite" data-testid="signing-room-status">
            {t(`contracts.signingRoom.${room.phase}`, { provider, attempt: room.attempt, max: MAX_SIGNING_ROOM_ATTEMPTS })}
          </p>
          {room.error && <div className="alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2" role="alert">
            <span>{t(`contracts.signingRoom.${errorKey}`)}</span>
            <button type="button" className="btn btn-outline-primary flex-shrink-0" onClick={room.retry}>
              {t('contracts.signingRoom.retry')}
            </button>
          </div>}
          {/* Allow Zigned to request cookie access after a click when the browser requires it. */}
          {room.session && <iframe
            ref={room.iframeRef}
            key={room.frameKey}
            src={room.session.signingUrl}
            title={t('contracts.signingRoom.frameTitle', { provider })}
            className={`signing-room-frame${room.phase === 'confirming' ? ' d-none' : ''}`}
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-downloads allow-storage-access-by-user-activation"
          />}
        </>}
      </section>
    </DashboardLayout>
  );
}
